// ============================================================================
// Feedback System - Replaces native browser dialogs (alert/confirm/prompt)
// ============================================================================
// 
// This module provides SSR-safe alternatives to native browser dialogs:
// - Toast notifications for success/error/info messages
// - Confirm dialog for yes/no decisions
// - Prompt dialog for user text input
//
// Setup:
// Wrap your app with FeedbackProvider in the layout:
//   import { FeedbackProvider } from "@/components/feedback";
//   <FeedbackProvider>{children}</FeedbackProvider>
//
// Usage:
//   import { useToast, useConfirm, usePrompt } from "@/components/feedback";
//   
//   const toast = useToast();
//   toast.success("Done!");
//   toast.error("Failed!");
//   toast.info("FYI...");
//   
//   const confirm = useConfirm();
//   const confirmed = await confirm({ title: "Delete?", message: "Are you sure?" });
//   
//   const prompt = usePrompt();
//   const value = await prompt({ title: "Name", message: "Enter name:" });
// ============================================================================

export { ToastProvider, useToast, notify } from "./ToastProvider";
export { ConfirmProvider, useConfirm, type ConfirmOptions } from "./useConfirm";
export { PromptProvider, usePrompt, type PromptOptions } from "./usePrompt";

import type { ReactNode } from "react";
import { ToastProvider } from "./ToastProvider";
import { ConfirmProvider } from "./useConfirm";
import { PromptProvider } from "./usePrompt";

// Combined provider for convenience
export function FeedbackProvider({ children }: { children: ReactNode }) {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <PromptProvider>
          {children}
        </PromptProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}
